/**
 * Script Registry
 *
 * Per-scene registry mapping @type strings to script schemas and factories.
 * Populated during scene loading from the scene JSON's `scripts` field.
 */

import { Logger } from "../utils/Logger";
import type { FieldSchema } from '../scenedata/schemas/types';
import type { ScriptFactory } from './ScriptCompiler';
import { compileScriptFactory, ScriptCompileError } from './ScriptCompiler';
import { inferScriptSchema } from './inferScriptSchema';
import { BehaviorData } from '../scenedata/behaviordata';
import { ScriptBehavior } from './ScriptBehavior';

export class ScriptRegistry {
    private _factories = new Map<string, ScriptFactory>();
    private _errors = new Map<string, string>();
    private _schemas = new Map<string, FieldSchema[]>();
    private _executionEnabled = true;
    private _registrationStarted = false;

    /** Whether registrations may produce executable factories. */
    get executionEnabled(): boolean { return this._executionEnabled; }

    /**
     * Author source/schema/data without compiling or executing script code.
     * Call before registering or loading scripts. This is irreversible for
     * the registry's lifetime, including clear() and scene reloads.
     */
    disableExecution(): void {
        if (!this._executionEnabled) return;
        if (this._registrationStarted) {
            throw new Error('Disable script execution before registering or loading scripts');
        }
        this._executionEnabled = false;
    }

    /**
     * Register a script from TypeScript source.
     * Infers the schema; compilation is skipped when execution is disabled.
     */
    register(typeName: string, typeScriptSource: string): void {
        this._registrationStarted = true;
        if (!this._executionEnabled) {
            this._schemas.set(typeName, inferScriptSchema(typeScriptSource));
            return;
        }
        try {
            const factory = compileScriptFactory(typeScriptSource);
            this._factories.set(typeName, factory);

            // Infer schema from the Data interface
            const schema = inferScriptSchema(typeScriptSource);
            this._schemas.set(typeName, schema);

            Logger.log(`[ScriptRegistry] Registered script: ${typeName} (${schema.length} fields)`);
        } catch (err) {
            const msg = err instanceof ScriptCompileError
                ? err.violations.join('; ')
                : String(err);
            this._errors.set(typeName, msg);
            Logger.error(`[ScriptRegistry] Failed to compile ${typeName}: ${msg}`);
        }
    }

    /** Look up a script factory by @type. Returns undefined if not a script. */
    getFactory(typeName: string): ScriptFactory | undefined {
        return this._factories.get(typeName);
    }

    /** Check if a type name is a known script type (even if it failed compilation). */
    isScriptType(typeName: string): boolean {
        return this._factories.has(typeName) || this._errors.has(typeName) || this._schemas.has(typeName);
    }

    /** Get compilation error for a script type (if any). */
    getError(typeName: string): string | undefined {
        return this._errors.get(typeName);
    }

    /** Get inferred schema for a script type (from its Data interface). */
    getSchema(typeName: string): FieldSchema[] | undefined {
        return this._schemas.get(typeName);
    }

    /**
     * Create a ScriptBehavior instance for the given type.
     * Builds default data from the inferred schema, applies overrides,
     * and calls load(). Returns null if the type is not a registered script.
     */
    createBehavior(
        sceneObject: import("../core/SceneObject").SceneObject,
        typeName: string,
        overrides?: Partial<Record<string, unknown>> | BehaviorData,
    ): ScriptBehavior | null {
        const factory = this._factories.get(typeName);
        if (!factory && (this._executionEnabled || !this._schemas.has(typeName))) return null;

        const data: BehaviorData = { '@type': typeName };
        const record = data as unknown as Record<string, unknown>;

        const schema = this._schemas.get(typeName);
        if (schema) {
            for (const field of schema) {
                if (field.default !== undefined) {
                    record[field.key] = structuredClone(field.default);
                }
            }
        }
        if (overrides) {
            for (const [key, value] of Object.entries(overrides)) {
                if (value !== undefined) record[key] = value;
            }
        }

        const sb = new ScriptBehavior(sceneObject, data, factory);
        sb.load();
        return sb;
    }

    /** Clear all registered scripts (called on scene teardown). */
    clear(): void {
        this._factories.clear();
        this._errors.clear();
        this._schemas.clear();
    }
}
